/**
 * useAllAccess — Canonical source of truth for "does this user have All Access?".
 *
 * Why this exists:
 * Earlier each component had its own check (some looked at courseAccess[code], some
 * at pricing_info, some compared enrollment counts) and they drifted apart, causing
 * confusing UX bugs like the Dashboard upsell widget showing to users who already
 * had All Access (May 2026 — manubanna0000@gmail.com report). This hook centralises
 * the check against the backend's single source of truth:
 *     GET /api/payments/user-pricing-info  →  { has_all_access: bool, owned_courses: [...] }
 *
 * Usage:
 *     const { hasAllAccess, ownedCourses, loading, refresh } = useAllAccess();
 *
 * `hasAllAccess` is `null` while loading so components can render a neutral state
 * (not the freemium upsell, not the unlocked banner) until we know for sure.
 */
import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

export const useAllAccess = () => {
  const [hasAllAccess, setHasAllAccess] = useState(null);
  const [ownedCourses, setOwnedCourses] = useState([]);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getUserPricingInfo();
      setPricingInfo(data);
      setHasAllAccess(Boolean(data?.has_all_access));
      setOwnedCourses(data?.owned_courses || []);
    } catch (e) {
      // On error, assume NO All Access — safer for paywalled UI
      setHasAllAccess(false);
      setOwnedCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Convenience flags for SKUs that are NOT in the All Access bundle.
  // Mock Tests + Projects are sold separately, so widgets need to gate on
  // these explicitly (don't piggyback on hasAllAccess).
  const ownsMockTests = Array.isArray(ownedCourses) && ownedCourses.includes('MOCK_TESTS');

  return { hasAllAccess, ownedCourses, pricingInfo, ownsMockTests, loading, refresh };
};

/**
 * Helper: given a course code and ownedCourses array + hasAllAccess flag,
 * decide if user has access. Mirrors backend `is_inside_all_access()` so the
 * two never drift.
 */
export const ALL_ACCESS_EXCLUDED_SKUS = new Set(['MOCK_TESTS', 'PROJECTS_ACCESS']);

export const isCoveredByAllAccess = (courseCode) => {
  if (!courseCode) return false;
  if (ALL_ACCESS_EXCLUDED_SKUS.has(courseCode)) return false;
  if (typeof courseCode === 'string' && courseCode.startsWith('PROJECT_')) return false;
  return true;
};

export const hasCourseAccess = (courseCode, { hasAllAccess, ownedCourses }) => {
  if (hasAllAccess && isCoveredByAllAccess(courseCode)) return true;
  return Array.isArray(ownedCourses) && ownedCourses.includes(courseCode);
};

export default useAllAccess;
